import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import CommunicationHeader from './header/CommunicationHeader';
import {
  getCommunicationParticipantRns,
  getParticipantKind,
  getParticipantName,
  getParticipantResourceRn,
  getParticipantType,
} from './communicationUtils';
import AllList from './types/all/AllList';
import CallList from './types/call/CallList';
import ChatList from './types/chat/ChatList';
import EmailList from './types/email/EmailList';
import InternalList from './types/internal/InternalList';
import SmsList from './types/sms/SmsList';

const CommunicationPanel = (props) => {
  const {
    caseData,
    participantDetails = {},
  } = props;
  const [selectedParticipantRn, setSelectedParticipantRn] = React.useState(null);
  const [selectedMethod, setSelectedMethod] = React.useState('ALL');

  const participantRns = React.useMemo(() => {
    return getCommunicationParticipantRns(caseData);
  }, [caseData]);
  const selectedParticipantResourceRn = selectedParticipantRn ? getParticipantResourceRn(selectedParticipantRn) : null;
  const selectedParticipantKind = selectedParticipantRn ? getParticipantKind(selectedParticipantRn) : null;
  const selectedParticipantType = selectedParticipantRn ? getParticipantType(selectedParticipantRn) : '';
  const selectedParticipantName = selectedParticipantRn ? getParticipantName(participantDetails, selectedParticipantRn) : '';

  React.useEffect(() => {
    if (participantRns.length === 0) {
      setSelectedParticipantRn(null);
      return;
    }

    if (!selectedParticipantRn || !participantRns.includes(selectedParticipantRn)) {
      setSelectedParticipantRn(participantRns[0]);
    }
  }, [participantRns, selectedParticipantRn]);

  React.useEffect(() => {
    setSelectedMethod('ALL');
  }, [selectedParticipantRn]);

  const renderActiveType = () => {
    switch (selectedMethod) {
      case 'INTERNAL':
        return (
          <InternalList
            selectedParticipantKind={selectedParticipantKind}
            selectedParticipantResourceRn={selectedParticipantResourceRn}
          />
        );
      case 'EMAIL':
        return (
          <EmailList
            selectedParticipantKind={selectedParticipantKind}
            selectedParticipantResourceRn={selectedParticipantResourceRn}
          />
        );
      case 'SMS':
        return (
          <SmsList
            selectedParticipantKind={selectedParticipantKind}
            selectedParticipantResourceRn={selectedParticipantResourceRn}
          />
        );
      case 'CALL':
        return (
          <CallList
            selectedParticipantKind={selectedParticipantKind}
            selectedParticipantResourceRn={selectedParticipantResourceRn}
          />
        );
      case 'CHAT':
        return (
          <ChatList
            selectedParticipantKind={selectedParticipantKind}
            selectedParticipantRn={selectedParticipantRn}
            selectedParticipantResourceRn={selectedParticipantResourceRn}
          />
        );
      default:
        return (
          <AllList
            selectedParticipantKind={selectedParticipantKind}
            selectedParticipantResourceRn={selectedParticipantResourceRn}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: {
          xs: '320px',
          md: 0,
        },
        backgroundColor: '#FFFFFF',
      }}
    >
      {
        !caseData && (
          <Box sx={{p: 2}}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#6D7280',
              }}
            >
              <Translate expression='case.Communication.NoCaseSelected' />
            </Typography>
          </Box>
        )
      }
      {
        caseData && (
          <>
            <Box
              sx={{
                flexShrink: 0,
                p: '22px 18px 16px 21px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <CommunicationHeader
                participantDetails={participantDetails}
                participantRns={participantRns}
                selectedMethod={selectedMethod}
                selectedParticipantName={selectedParticipantName}
                selectedParticipantRn={selectedParticipantRn}
                selectedParticipantType={selectedParticipantType}
                setSelectedMethod={setSelectedMethod}
                setSelectedParticipantRn={setSelectedParticipantRn}
              />
            </Box>
            <Stack
              sx={{
                'gap': 1,
                'flex': 1,
                'minHeight': 0,
                'overflowY': 'auto',
                'p': '2px 18px 18px 21px',
                'scrollbarColor': '#CFCFD4 transparent',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#CFCFD4',
                  borderRadius: '999px',
                },
              }}
            >
              {
                selectedParticipantKind === 'OTHER' && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#6D7280',
                    }}
                  >
                    <Translate expression='case.Communication.UnsupportedParticipant' />
                  </Typography>
                )
              }
              {
                ['CLIENT', 'CAREGIVER'].includes(selectedParticipantKind) && renderActiveType()
              }
            </Stack>
          </>
        )
      }
    </Box>
  );
};

CommunicationPanel.propTypes = {
  caseData: PropTypes.object,
  participantDetails: PropTypes.object,
};

export default React.memo(CommunicationPanel);
